import React, { useState, useEffect, useContext, createContext, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import DemoTooltip from './DemoTooltip';
import { isDemoUser } from '../../utils/demoUtils';
import { BusinessContext } from '../../context/BusinessContext';

// Create context for tooltip management
const DemoTooltipContext = createContext();

export const useDemoTooltips = () => {
  const context = useContext(DemoTooltipContext);
  return context || {};
};

const TOOLTIP_STORAGE_KEY = 'demo_tooltips_dismissed';

// Define all tooltips with their properties
const TOOLTIP_DEFINITIONS = [
  {
    id: 'welcome',
    target: '[data-demo-target="dashboard-main"]',
    title: '🎉 Welcome to Your Analytics Dashboard!',
    content: 'You\'re seeing real business data that updates daily. Try clicking on charts to explore insights, or use the sidebar to see different views. This demo shows exactly how the platform works for our customers.',
    placement: 'top-start',
    delay: 0,
    maxWidth: 350,
    priority: 1
  },
  {
    id: 'view-toggle',
    target: '[data-demo-target="view-toggle"]',
    title: '💰 Revenue vs Rating View',
    content: 'Switch between Revenue Impact (how reviews affect your sales) and Rating Impact (how it affects your Google rating) to see different perspectives on your data.',
    placement: 'bottom',
    delay: 0,
    maxWidth: 300,
    priority: 2
  },
  {
    id: 'attributes-chart',
    target: '[data-demo-target="attributes-chart"]',
    title: '🎯 Specific Qualities',
    content: 'Displays the specific qualities customers mention about each topic. Perfect for understanding what to improve or what you\'re doing well.',
    placement: 'top',
    delay: 0,
    maxWidth: 280,
    priority: 3
  },
  {
    id: 'timeline-chart',
    target: '[data-demo-target="timeline-chart"]',
    title: '📈 Trends Over Time',
    content: 'Track how customer sentiment and revenue impact change month by month. Spot trends and seasonal patterns.',
    placement: 'top',
    delay: 0,
    maxWidth: 280,
    priority: 4
  },
  {
    id: 'comments-panel',
    target: '[data-demo-target="comments-panel"]',
    title: '💬 Customer Comments',
    content: 'Real customer comments automatically organized by positive and negative sentiment for quick insights.',
    placement: 'top',
    delay: 0,
    maxWidth: 280,
    priority: 5
  },
  {
    id: 'categories-overview',
    target: '[data-demo-target="categories-overview"]',
    title: '📋 Business Categories',
    content: 'This filters the dashboard by the selected product or service category. Click any category to drill down and see specific details for that area of your business.',
    placement: 'right',
    delay: 0,
    maxWidth: 280,
    priority: 6
  },
  {
    id: 'actions-panel',
    target: '[data-demo-target="actions-panel"]',
    title: '🚀 AI Recommendations',
    content: 'AI-powered recommendations based on your review analysis. These actionable insights help you improve customer satisfaction and revenue.',
    placement: 'left',
    delay: 0,
    maxWidth: 280,
    priority: 7
  },
  {
    id: 'sidebar-nav',
    target: '[data-demo-target="sidebar-nav"]',
    title: '🧭 Navigation Menu',
    content: 'Explore different views: Reviews for individual comments, Timeline for trends, Categories for customization, and Businesses for multi-location management.',
    placement: 'right',
    delay: 0,
    maxWidth: 280,
    priority: 8
  }
];

const DemoTooltipManager = ({ children }) => {
  const [dismissedTooltips, setDismissedTooltips] = useState(new Set());
  const [activeTooltipId, setActiveTooltipId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const { selectedBusinesses } = useContext(BusinessContext);
  const [userEmail, setUserEmail] = useState('');
  const location = useLocation();

  // Check if user is demo user
  useEffect(() => {
    const checkDemoUser = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/users/`, { 
          credentials: 'include' 
        });
        if (response.ok) {
          const userData = await response.json();
          setUserEmail(userData.email);
        }
      } catch (error) {
        console.error('Error checking user:', error);
      }
    };
    checkDemoUser();
  }, []);



  // Load dismissed tooltips from localStorage
  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(TOOLTIP_STORAGE_KEY);
      if (dismissed) {
        setDismissedTooltips(new Set(JSON.parse(dismissed)));
      }
    } catch (error) {
      console.error('Error loading dismissed tooltips:', error);
    }
    setIsInitialized(true);
  }, []);

  // Save dismissed tooltips to localStorage
  const saveDismissedTooltips = (dismissed) => {
    try {
      localStorage.setItem(TOOLTIP_STORAGE_KEY, JSON.stringify([...dismissed]));
    } catch (error) {
      console.error('Error saving dismissed tooltips:', error);
    }
  };

  const handleTooltipDismiss = useCallback((tooltipId) => {
    const newDismissed = new Set(dismissedTooltips);
    newDismissed.add(tooltipId);
    setDismissedTooltips(newDismissed);
    saveDismissedTooltips(newDismissed);
    setActiveTooltipId(null);
    
    // Auto-show next tooltip immediately with retry mechanism
    setTimeout(() => {
      // We'll call showNextTooltip here but need to avoid circular dependency
      if (!isDemoUser(userEmail)) return;

      // Find next tooltip to show (lowest priority number not yet dismissed)
      const availableTooltips = TOOLTIP_DEFINITIONS
        .filter(tooltip => !newDismissed.has(tooltip.id))
        .sort((a, b) => a.priority - b.priority);

      if (availableTooltips.length > 0) {
        const nextTooltip = availableTooltips[0];
        
        // Use recursive retry logic
        const tryShowTooltip = (retryCount = 0) => {
          const targetExists = document.querySelector(nextTooltip.target);
          if (targetExists) {
            setActiveTooltipId(nextTooltip.id);
          } else if (retryCount < 10) {
            setTimeout(() => tryShowTooltip(retryCount + 1), 500);
          }
          // If still not found after retries, just skip it
        };
        
        tryShowTooltip(0);
      }
    }, 300); // Short delay to allow DOM to update
  }, [dismissedTooltips, userEmail]);

  const showNextTooltip = useCallback((retryCount = 0) => {
    if (!isDemoUser(userEmail)) {
      return;
    }

    // Find next tooltip to show (lowest priority number not yet dismissed)
    const availableTooltips = TOOLTIP_DEFINITIONS
      .filter(tooltip => !dismissedTooltips.has(tooltip.id))
      .sort((a, b) => a.priority - b.priority);

    if (availableTooltips.length > 0) {
      const nextTooltip = availableTooltips[0];
      
      // Check if target element exists AND is valid for MUI Popper
      const targetElement = document.querySelector(nextTooltip.target);
      
      let isValidAnchor = false;
      if (targetElement) {
        const computedStyle = window.getComputedStyle(targetElement);
        const rect = targetElement.getBoundingClientRect();
        
        // Check if element is truly part of document layout for MUI
        isValidAnchor = (
          targetElement.offsetParent !== null && // Not display: none
          computedStyle.opacity !== '0' && // Not invisible
          computedStyle.visibility !== 'hidden' && // Not visibility: hidden
          rect.width > 0 && rect.height > 0 && // Has dimensions
          targetElement.isConnected // Connected to DOM
        );
      }
      
      if (targetElement && isValidAnchor) {
        setActiveTooltipId(nextTooltip.id);
      } else {
        // If target doesn't exist and we haven't retried too many times, retry
        if (retryCount < 10) {
          setTimeout(() => showNextTooltip(retryCount + 1), 500);
        } else {
          // If we've retried too many times, mark as dismissed and try next
          handleTooltipDismiss(nextTooltip.id);
        }
      }
    }
  }, [userEmail, dismissedTooltips, handleTooltipDismiss, activeTooltipId]);

  // Start showing tooltips after user has businesses selected
  useEffect(() => {
    if (!isInitialized || !isDemoUser(userEmail) || activeTooltipId) return;
    
    // Only show tooltips on dashboard page
    if (location.pathname !== '/') {
      return;
    }
    
    // Wait for businesses to be loaded and dashboard to be ready
    if (selectedBusinesses && selectedBusinesses.length > 0) {
      // Wait for dashboard fade-in animation to complete (1 second) + extra buffer
      setTimeout(() => {
        showNextTooltip();
      }, 1300); // 1300ms to ensure animation is fully complete and element is stable
    }
  }, [isInitialized, userEmail, selectedBusinesses, activeTooltipId, showNextTooltip, location.pathname]);

  const resetTooltips = () => {
    setDismissedTooltips(new Set());
    setActiveTooltipId(null);
    localStorage.removeItem(TOOLTIP_STORAGE_KEY);
    // Restart the tooltip sequence
    setTimeout(() => {
      showNextTooltip();
    }, 1000);
  };

  const skipAllTooltips = () => {
    const allIds = TOOLTIP_DEFINITIONS.map(t => t.id);
    const allDismissed = new Set(allIds);
    setDismissedTooltips(allDismissed);
    saveDismissedTooltips(allDismissed);
    setActiveTooltipId(null);
  };

  // Only render if user is demo user
  if (!isDemoUser(userEmail)) {
    return (
      <DemoTooltipContext.Provider value={{ resetTooltips: () => {}, skipAllTooltips: () => {} }}>
        {children}
      </DemoTooltipContext.Provider>
    );
  }

  const activeTooltip = TOOLTIP_DEFINITIONS.find(t => t.id === activeTooltipId);
  
  // Calculate current step and total steps
  const currentStep = activeTooltip ? activeTooltip.priority : 0;
  const totalSteps = TOOLTIP_DEFINITIONS.length;

  return (
    <DemoTooltipContext.Provider value={{ resetTooltips, skipAllTooltips }}>
      {children}
      {activeTooltip && (
        <DemoTooltip
          key={activeTooltip.id}
          id={activeTooltip.id}
          target={activeTooltip.target}
          title={activeTooltip.title}
          content={activeTooltip.content}
          placement={activeTooltip.placement}
          delay={activeTooltip.delay}
          maxWidth={activeTooltip.maxWidth}
          arrow={true}
          onDismiss={handleTooltipDismiss}
          onSkipAll={skipAllTooltips}
          currentStep={currentStep}
          totalSteps={totalSteps}
          open={true}
        />
      )}
    </DemoTooltipContext.Provider>
  );
};

export default DemoTooltipManager; 